import { useAppSelector } from "@/OneLiners";
import { ActionResponse, ActionStatusHelper, FeedBackType, LONG_PROCESS_TOAST_DURATION, ResponseHook, useAppEnum, useAppNavigation } from "@/Shared";
import { useEffect, useState } from "react";
import { UserFormViewModel } from "../ViewModels";
import { PasswordFormValues, PasswordValidationSchema } from "../UserFormValidation";
import { AuthService } from "@/Domains/Auth";
import { ToastHook } from "@/Shared/Logic/Hooks/TechnicalHooks/useToast";
import { useUserEnricher } from "./useUserEnricher";

/**
 * usePasswordForm hook – Manages user list configuration, state, and interactions.
 *
 * This hook encapsulates all the logic required to:
 * - Fetch and enrich user entities
 * - Provide configuration for a DataTable or DataCardMap component
 * - Handle row actions: view, create, edit, delete
 * - Manage toast notifications for user feedback
 * - Report fetch/delete status for conditional UI rendering
 *
 * Exposes:
 * - userConfig: DataTableConfig object used by UI components
 * - isFetchRunning, isFetchFailed, fetchErrorMessage: status of fetch operation
 * - isDeleteSucceeded, isDeleteFailed, deleteErrorMessage: status of deletion
 * - setDeleteIdle: resets delete status to 'idle' (used to clear state)
 * 
 * Internally, it:
 * - Enriches users using `useEnrichedUser`
 * - Builds table headers with user data and enums
 * 
 * Author: Samano CASTRE  
 * Date: August 3, 2025
 */
export function usePasswordForm(toastHook : ToastHook) {

	const { goToLogOut } = useAppNavigation();
	const currentUser = useAppSelector(state => state.sessionState.userSession?.createdByUser) ?? undefined;

	/**
	 * User domain operations and related backend state.
	 * Exposes CRUD actions, current entity fetching and associated responses.
	 */
	const {
			userMap,
			enricherResponse,
		} = useUserEnricher();

	/**
	 * User-related enums exposed to the UI layer.
	 * Used to build the form view model.
	 */
	const { UserEnums } = useAppEnum();


	const [entityFormVM, setFormVM] = useState<UserFormViewModel>();

	const [formResponse, setFormResponse] = useState<ActionResponse>({status : 'idle'});

	useEffect(() => {
		 if (ActionStatusHelper.isSucceeded(enricherResponse.status)) {
			setFormVM(new UserFormViewModel(UserEnums, userMap, currentUser));
		}
	}, [
		enricherResponse.status,
		currentUser
	]);

	// Toast management based on form status
	useEffect(() => {
		const { isLoading, isFailed, isSucceeded } = ActionStatusHelper.is(formResponse.status);
		if (isSucceeded) {
			openToast(
				'auth.update.password.success',
				FeedBackType.SUCCESS,
				goToLogOut
			);
		}
		if (isFailed) {
			openToast(
				 formResponse.error || "",
				FeedBackType.ERROR
			);
		}

		if (isLoading) {
			toastHook.openToast({
				data : {
					message : "Soumission en cours...",
				   type : FeedBackType.INFO,
				   duration : LONG_PROCESS_TOAST_DURATION
				}
			}
			);
		}

	}, [formResponse.status, formResponse.error, entityFormVM]);


	const openToast = (message: string, type: FeedBackType, onClose?: () => void) => {
        toastHook.openToast({ data: { message, type }, onClose });
    };


	/**
	 * handleSubmit – Handles form submission for creating or updating a user.
	 * 
	 * Logic:
	 * - If `currentUser` has an `id`, it assumes the form is in edit mode and calls `updateUser`
	 * - Otherwise, it creates a new user via `createUser`
	 * 
	 * @param values - The form values entered by the user
	 */
	const handleSubmit = async (values: PasswordFormValues) => {
		ResponseHook.loading(setFormResponse);
		AuthService.updatePassword(values).then(async data => {
			ResponseHook.success(setFormResponse);
		}).catch(error =>
			ResponseHook.error(setFormResponse, error, "Erreur lors de la mise à jour du mot de passe")
		);
	};
	
	
	return {
		// Form
		entityFormVM,
		handleSubmit,
		formResponse,
		PasswordValidationSchema: PasswordValidationSchema(),
		response : enricherResponse,
	}
}